import { ReactElement } from 'react';

import { MenuItemProps } from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { CustomSelect, CustomSelectProps } from '../custom/select';

export type SelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
> = Omit<CustomSelectProps<LabelKey, ValueKey, Label, Value>, 'inputRef'> & {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
};

export const Select = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
>({
  ControllerProps,
  ...props
}: SelectProps<
  TFieldValues,
  TName,
  LabelKey,
  ValueKey,
  Label,
  Value
>): ReactElement => {
  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => (
        <CustomSelect
          {...props}
          {...field}
          inputRef={ref}
          error={props.error || Boolean(error)}
          helperText={error?.message || props.helperText}
          onChange={(event, action) => {
            onChange(event);
            props.onChange && props.onChange(event, action);
          }}
        />
      )}
    />
  );
};
